import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { PostHogProvider } from 'posthog-js/react'
import { posthog, initPostHog, isPostHogEnabled, applyFlagEnvironment } from './posthog'
import { useAccountStore } from '@/stores/account-store'
import { pickLiveSubscription, resolvePlanTier } from '@/context/plan-constraint-utils'
import { usePlanTierStore } from '@/stores/plan-tier-store'
import { personPropsChanged } from './person-dedupe'
import { consumeLoginSubmitted } from './login-marker'
import { trackEvent } from './use-analytics'
import { EVENTS } from './events'

/**
 * Wraps children in PostHogProvider when configured.
 * When VITE_POSTHOG_KEY is empty (dev default), renders children directly — no PostHog overhead.
 */
export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const initialized = useRef(false)
  // Child effects run before this component's own effect, so a session that
  // is already restored at mount would reach identify() before init() and be
  // silently dropped, leaving the user anonymous and every email-targeted
  // flag off. AnalyticsIdentify mounts only once PostHog is live.
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!initialized.current && isPostHogEnabled()) {
      initPostHog()
      initialized.current = true
      setReady(true)
    }
  }, [])

  if (!isPostHogEnabled()) {
    return <>{children}</>
  }

  return (
    <PostHogProvider client={posthog}>
      {ready && <AnalyticsIdentify />}
      {children}
    </PostHogProvider>
  )
}

/**
 * Identifies the current user with PostHog when auth state changes.
 * Resets identity on logout.
 */
function AnalyticsIdentify() {
  const session = useAccountStore((s) => s.session)
  const user = useAccountStore((s) => s.user)
  const publishedPlanTier = usePlanTierStore((s) => s.planTier)
  const identifiedRef = useRef<string | null>(null)

  // Identify on auth
  useEffect(() => {
    if (!session) {
      if (identifiedRef.current) {
        posthog.reset()
        // reset() wipes the flag-evaluation overrides too.
        applyFlagEnvironment()
        identifiedRef.current = null
      }
      return
    }

    // Identify on the stable edgeUserId (== sub until a pool migration → no alias()).
    const id = session.user.edgeUserId
    if (identifiedRef.current === id) return

    posthog.identify(id, {
      email: session.user.email,
      name: `${session.user.givenName} ${session.user.familyName}`.trim(),
      beta_access: session.user.betaAccess,
    })
    identifiedRef.current = id
    if (consumeLoginSubmitted()) trackEvent(EVENTS.USER_LOGGED_IN, {})
  }, [session])

  // Enrich with profile data when user profile loads
  useEffect(() => {
    if (!user || !session || !identifiedRef.current) return

    // The tier every gate uses (published by PlanConstraintProvider), resolved
    // from the live subscription until the provider has mounted.
    // A lapsed user has no live row; describe their latest one instead.
    const live = pickLiveSubscription(user.subscriptions) ?? user.subscriptions?.[0]
    const planName = live?.planDetails?.name ?? null
    const planTier = publishedPlanTier ?? resolvePlanTier({ subscriptions: user.subscriptions })
    const subscriptionStatus = live?.subscriptionStatus ?? null

    const props = {
      // Also sent with identify(); repeated here because a dropped $identify (seen
      // 2026-09-28 on Brave) otherwise leaves the person with no email for good.
      email: session.user.email,
      name: `${session.user.givenName} ${session.user.familyName}`.trim(),
      plan_tier: planTier,
      plan_name: planName,
      subscription_status: subscriptionStatus,
    }
    // The profile reloads often and each set() is a billed $set event; send
    // only when a value actually changed for this user (person-dedupe.ts).
    if (!personPropsChanged(identifiedRef.current, props)) return
    posthog.people.set(props)
  }, [user, session, publishedPlanTier])

  return null
}
